import { EpisodeStatusTag } from '@/components/episodes/EpisodeStatusTag';
import { useGetSearchParam, useGetSearchParams } from '@/hooks/router';
import { FeatureFlag } from '@/types/featureFlag';
import { getAllEpisodesLabel, getEpisodeLabel } from '@/utils/episodeLabel';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { useSuspenseQuery } from '@apollo/client/react';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import invariant from 'tiny-invariant';

import { patientEpisodeViewEpisodeAtom } from '../PatientEpisodeView.atoms';
import { formatEpisodeWindow } from '../PatientEpisodeViewHeader';
import { PatientEpisodeSelectorViewQuery } from './PatientEpisodeSelector.gql.types';

const EPISODE_FILTER_PARAMS = ['discipline', 'status'] as const;

export const PATIENT_EPISODE_SELECTOR_QUERY = gql`
    query PatientEpisodeSelectorView($patientId: UUID!) {
        patient(id: $patientId) {
            id
            episodes(ordering: { startDate: DESC }) {
                typeLabel
                items {
                    id
                    startDate
                    endDate
                    status
                    numberInSequence
                    typeLabel
                    recertWindow {
                        startDate
                        endDate
                    }
                    patient {
                        id
                        lastName
                    }
                }
            }
        }
    }
`;

type EpisodeMenuItemProps = {
    episode: NonNullable<
        NonNullable<PatientEpisodeSelectorViewQuery['patient']>['episodes']
    >['items'][number];
    onSelect(episodeId: string): void;
    isSelected: boolean;
};

const EpisodeMenuItem: FC<EpisodeMenuItemProps> = (props) => {
    const { episode, onSelect, isSelected } = props;
    const [params] = useGetSearchParams();
    const path = useMemo(() => {
        const p = new URLSearchParams(params.toString());
        p.set('episode', episode.id);
        EPISODE_FILTER_PARAMS.forEach((k) => p.delete(k));
        return `?${p.toString()}`;
    }, [params, episode.id]);

    const onClick = useCallback(() => {
        onSelect(episode.id);
    }, [episode.id, onSelect]);

    return (
        <MenuItem
            sx={{
                paddingLeft: 2,
                paddingRight: 2,
                gap: 2,
            }}
            LinkComponent={Link}
            component={Link}
            to={path}
            onClick={onClick}
            selected={isSelected}
        >
            <Typography>{getEpisodeLabel(episode.typeLabel, episode.numberInSequence)}</Typography>
            <EpisodeStatusTag status={episode.status} />
            {episode.startDate && episode.endDate ? (
                <Typography fontWeight={300}>
                    {formatEpisodeWindow(episode.startDate, episode.endDate)}
                </Typography>
            ) : null}
        </MenuItem>
    );
};

type PatientEpisodeSelectorProps = {
    patientId: string;
    open: boolean;
    anchorEl: null | HTMLElement;
    handleClose(): void;
};

const AllEpisodesMenuItem: FC<{
    count: number;
    isSelected: boolean;
    onSelect(): void;
    typeLabel: string;
}> = ({ count, isSelected, onSelect, typeLabel }) => {
    const [params] = useGetSearchParams();
    const path = useMemo(() => {
        const p = new URLSearchParams(params.toString());
        p.set('episode', 'all');
        EPISODE_FILTER_PARAMS.forEach((k) => p.delete(k));
        return `?${p.toString()}`;
    }, [params]);

    return (
        <MenuItem
            sx={{ paddingLeft: 2, paddingRight: 2, gap: 2 }}
            LinkComponent={Link}
            component={Link}
            to={path}
            onClick={onSelect}
            selected={isSelected}
        >
            <Typography>{getAllEpisodesLabel(typeLabel, count)}</Typography>
        </MenuItem>
    );
};

export const PatientEpisodeSelector: FC<PatientEpisodeSelectorProps> = (props) => {
    const { patientId, open, anchorEl, handleClose } = props;
    const episode = useAtomValue(patientEpisodeViewEpisodeAtom);
    const [episodeParam] = useGetSearchParam('episode');
    const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(
        episodeParam?.toLowerCase() ?? episode?.id ?? null,
    );
    // MUI's Modal only removes the menu portal once the Grow exit transition
    // reports onExited; that callback can be lost when a suspenseful router
    // navigation races the close, leaving the menu stuck over the page. Keep the
    // Menu mounted long enough for the exit animation, then force-unmount it.
    const [mounted, setMounted] = useState(open);
    if (open && !mounted) {
        // Mount synchronously on open so the Grow enter animation starts on the
        // first paint rather than after an extra render cycle.
        setMounted(true);
    }
    useEffect(() => {
        if (open) {
            return;
        }
        const timer = setTimeout(() => setMounted(false), 300);
        return () => clearTimeout(timer);
    }, [open]);
    const { data } = useSuspenseQuery<PatientEpisodeSelectorViewQuery>(
        PATIENT_EPISODE_SELECTOR_QUERY,
        {
            variables: { patientId },
        },
    );

    const onClick = useCallback(
        (episodeId: string) => {
            setSelectedEpisodeId(episodeId);
            handleClose();
        },
        [handleClose],
    );

    const onSelectAll = useCallback(() => {
        setSelectedEpisodeId('all');
        handleClose();
    }, [handleClose]);

    invariant(data.patient, 'Patient not found');
    invariant(data.patient.episodes, 'Episodes not found');
    const episodes = data.patient.episodes.items;
    const listTypeLabel = data.patient.episodes.typeLabel;
    const hasAllEpisodesView = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_ALL_EPISODES_VIEW);

    if (!mounted) {
        return null;
    }

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            {hasAllEpisodesView && episodes.length >= 1 && (
                <AllEpisodesMenuItem
                    count={episodes.length}
                    isSelected={selectedEpisodeId === 'all'}
                    onSelect={onSelectAll}
                    typeLabel={listTypeLabel}
                />
            )}
            {episodes.map((episode) => (
                <EpisodeMenuItem
                    key={episode.id}
                    episode={episode}
                    isSelected={selectedEpisodeId === episode.id}
                    onSelect={onClick}
                />
            ))}
        </Menu>
    );
};
